import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  CircularProgress, Alert, Button, Stack, Divider, Chip, Avatar,
  IconButton, Tooltip, LinearProgress, Link as MuiLink, useTheme,
  useMediaQuery, Snackbar, Fade
} from '@mui/material';
import {
  Share, Visibility, CalendarToday, Speed, Psychology,
  TrendingUp, PhotoCamera, Home, CheckCircle, Info, ContentCopy
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link, useParams } from 'react-router-dom';
import { getSharedClassification } from '../api/shared';
import TipDisplay from '../components/edutips/TipDisplay';

const SharedClassificationPage = () => {
  const { shareId } = useParams();
  const [classification, setClassification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchSharedClassification();
  }, [shareId]);

  const fetchSharedClassification = async () => {
    try {
      setLoading(true);
      const data = await getSharedClassification(shareId);
      setClassification(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch shared classification:', err);
      setError('This shared classification is no longer available or the link is invalid.');
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const getCategoryColor = (category) => {
    const colors = {
      'Recyclable': '#43a047',
      'Organic': '#66bb6a',
      'Hazardous': '#e53935',
      'General': '#546e7a',
      'Electronic': '#2196f3'
    };
    return colors[category] || '#546e7a';
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      'Recyclable': 'linear-gradient(135deg, #43a047, #66bb6a)',
      'Organic': 'linear-gradient(135deg, #66bb6a, #81c784)',
      'Hazardous': 'linear-gradient(135deg, #e53935, #ef5350)',
      'General': 'linear-gradient(135deg, #546e7a, #607d8b)',
      'Electronic': 'linear-gradient(135deg, #2196f3, #42a5f5)'
    };
    return gradients[category] || 'linear-gradient(135deg, #546e7a, #607d8b)';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Recyclable': '♻️',
      'Organic': '🌱',
      'Hazardous': '⚠️',
      'General': '🗑️',
      'Electronic': '🔌'
    };
    return icons[category] || '📦';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return '#43a047';
    if (confidence >= 70) return '#ff9800';
    return '#f44336';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 90) return 'Very High';
    if (confidence >= 70) return 'Good';
    if (confidence >= 50) return 'Moderate';
    return 'Low';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    try {
      const shareData = {
        title: `${classification.category?.name} Classification - Sortify`,
        text: `Check out this ${classification.category?.name} classification with ${classification.resultConfidence?.toFixed(1)}% confidence on Sortify!`,
        url: window.location.href
      };

      if (navigator.share && !isSmall) {
        await navigator.share(shareData);
        setSnackbarMessage('🔗 Shared successfully!');
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setSnackbarMessage('🔗 Share link copied to clipboard!');
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error sharing:', error);
      setSnackbarMessage('❌ Failed to share');
      setSnackbarOpen(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}>
        <Card elevation={24} sx={{
          p: { xs: 4, sm: 6 },
          borderRadius: 6,
          textAlign: 'center',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          <CircularProgress
            size={isSmall ? 50 : 70}
            sx={{ color: '#43a047', mb: 3 }}
            thickness={4}
          />
          <Typography variant={isSmall ? "h6" : "h5"} fontWeight={600} color="text.secondary">
            Loading shared classification...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, opacity: 0.7 }}>
            🌱 Powered by Sortify AI
          </Typography>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}>
        <Container maxWidth="sm">
          <Card elevation={24} sx={{
            p: { xs: 4, sm: 6 },
            borderRadius: 6,
            textAlign: 'center',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)'
          }}>
            <Typography variant="h1" sx={{ fontSize: { xs: '4rem', sm: '6rem' }, mb: 2 }}>
              🔗
            </Typography>
            <Typography variant={isSmall ? "h5" : "h4"} fontWeight={700} sx={{ mb: 2, color: '#e53935' }}>
              Link Not Found
            </Typography>
            <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
              {error}
            </Alert>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
              This classification might have been removed or the sharing link has expired.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                component={Link}
                to="/"
                variant="contained"
                size="large"
                startIcon={<Home />}
                sx={{
                  background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(67,160,71,0.3)'
                }}
              >
                Go to Sortify Home
              </Button>
              <Button
                component={Link}
                to="/classify"
                variant="outlined"
                size="large"
                startIcon={<PhotoCamera />}
                sx={{
                  borderColor: '#43a047',
                  color: '#43a047',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Try Classification
              </Button>
            </Stack>
          </Card>
        </Container>
      </Box>
    );
  }

  if (!classification) {
    return null;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 50%, #e0f2f1 100%)',
      pb: 4
    }}>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        {/* Header Section */}
        <Paper elevation={16} sx={{
          p: { xs: 3, sm: 4 },
          mb: { xs: 3, sm: 4 },
          borderRadius: { xs: 3, sm: 4 },
          background: 'linear-gradient(135deg, #43a047, #66bb6a)',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant={isSmall ? "h4" : "h3"} fontWeight={800} sx={{ mb: 1 }}>
              🌱 Sortify
            </Typography>
            <Typography variant={isSmall ? "body1" : "h6"} sx={{ opacity: 0.9 }}>
              Smart Waste Classification • AI-Powered Results
            </Typography>
          </Box>
        </Paper>

        {/* Action Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 4,
          gap: 2
        }}>
          <Box>
            <Typography variant={isSmall ? "h5" : "h4"} fontWeight={700} sx={{ color: '#43a047', mb: 1 }}>
              📊 Shared Classification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View this AI classification result shared from Sortify
            </Typography>
          </Box>

          <Tooltip title="Share this classification">
            <IconButton
              onClick={handleShare}
              size="large"
              sx={{
                background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                color: 'white',
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
                boxShadow: '0 8px 32px rgba(255,152,0,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f57c00, #ff9800)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Share />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {/* Image Section - Full width on mobile */}
          <Grid item xs={12} lg={7}>
            <Paper elevation={20} sx={{
              borderRadius: { xs: 3, sm: 4 },
              overflow: 'hidden',
              border: `4px solid ${getCategoryColor(classification.category?.name)}`,
              position: 'relative'
            }}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={classification.imageUrl || classification.imagePath}
                  alt="Waste Classification"
                  sx={{
                    width: '100%',
                    height: { xs: '300px', sm: '400px', md: '500px' },
                    objectFit: 'cover',
                    background: getCategoryGradient(classification.category?.name)
                  }}
                />

                {/* Floating Category Badge */}
                <Box sx={{
                  position: 'absolute',
                  top: { xs: 16, sm: 20 },
                  left: { xs: 16, sm: 20 },
                  zIndex: 2
                }}>
                  <Chip
                    icon={<span style={{ fontSize: isSmall ? '1.5rem' : '2rem' }}>{getCategoryIcon(classification.category?.name)}</span>}
                    label={classification.category?.name || 'Unknown'}
                    size={isSmall ? "medium" : "large"}
                    sx={{
                      background: getCategoryGradient(classification.category?.name),
                      color: 'white',
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                      px: { xs: 1.5, sm: 2 },
                      py: 0.5,
                      backdropFilter: 'blur(20px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                      border: '3px solid white',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                </Box>

                {/* Floating Confidence Badge */}
                <Box sx={{
                  position: 'absolute',
                  top: { xs: 16, sm: 20 },
                  right: { xs: 16, sm: 20 },
                  zIndex: 2
                }}>
                  <Chip
                    label={`${classification.resultConfidence?.toFixed(1)}%`}
                    size={isSmall ? "medium" : "large"}
                    sx={{
                      background: `linear-gradient(135deg, ${getConfidenceColor(classification.resultConfidence)}, ${alpha(getConfidenceColor(classification.resultConfidence), 0.8)})`,
                      color: 'white',
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                      px: { xs: 1.5, sm: 2 },
                      py: 0.5,
                      backdropFilter: 'blur(20px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                      border: '3px solid white'
                    }}
                  />
                </Box>

                {/* Sortify Watermark */}
                <Box sx={{
                  position: 'absolute',
                  bottom: { xs: 12, sm: 20 },
                  right: { xs: 12, sm: 20 },
                  background: 'rgba(0,0,0,0.8)',
                  color: 'white',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: { xs: 2, sm: 3 },
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant={isSmall ? "caption" : "body2"} fontWeight={600}>
                    🌱 Classified with Sortify AI
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Details Section */}
          <Grid item xs={12} lg={5}>
            <Stack spacing={{ xs: 2, sm: 3 }}>
              {/* Educational Tips Section - Show relevant tips for this category */}
              <Fade in timeout={1000}>
                <Box>
                  <TipDisplay
                    categoryId={classification.category?.id}
                    compact={true}
                    showRefresh={true}
                  />
                </Box>
              </Fade>

              {/* Main Classification Result */}
              <Paper elevation={12} sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: { xs: 3, sm: 4 },
                border: `3px solid ${alpha(getCategoryColor(classification.category?.name), 0.3)}`,
                background: `linear-gradient(135deg, ${alpha(getCategoryColor(classification.category?.name), 0.08)}, rgba(255,255,255,0.95))`,
                backdropFilter: 'blur(20px)'
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'center', sm: 'flex-start' },
                  gap: 2,
                  mb: 3,
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Box sx={{
                    fontSize: { xs: '3rem', sm: '4rem' },
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                  }}>
                    {getCategoryIcon(classification.category?.name)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant={isSmall ? "h4" : "h3"} fontWeight={800} sx={{
                      color: getCategoryColor(classification.category?.name),
                      mb: 1
                    }}>
                      {classification.category?.name}
                    </Typography>
                    <Chip
                      icon={<CheckCircle />}
                      label="AI Classification"
                      sx={{
                        background: alpha('#43a047', 0.15),
                        color: '#43a047',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Box>

                <Paper elevation={4} sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  background: alpha('#43a047', 0.08),
                  border: `2px solid ${alpha('#43a047', 0.2)}`,
                  mb: 3
                }}>
                  <Typography variant={isSmall ? "body1" : "h6"} color="text.primary" sx={{
                    lineHeight: 1.6,
                    fontWeight: 500
                  }}>
                    💡 {classification.suggestion || 'Proper disposal guidance not available'}
                  </Typography>
                </Paper>

                {/* Confidence Meter */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant={isSmall ? "subtitle1" : "h6"} fontWeight={700}>
                      🎯 AI Confidence
                    </Typography>
                    <Chip
                      label={getConfidenceLabel(classification.resultConfidence)}
                      size="small"
                      sx={{
                        background: alpha(getConfidenceColor(classification.resultConfidence), 0.15),
                        color: getConfidenceColor(classification.resultConfidence),
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={classification.resultConfidence}
                    sx={{
                      height: { xs: 12, sm: 16 },
                      borderRadius: 8,
                      backgroundColor: alpha(getConfidenceColor(classification.resultConfidence), 0.15),
                      '& .MuiLinearProgress-bar': {
                        background: `linear-gradient(90deg, ${getConfidenceColor(classification.resultConfidence)}, ${alpha(getConfidenceColor(classification.resultConfidence), 0.8)})`,
                        borderRadius: 8,
                        boxShadow: `0 0 20px ${alpha(getConfidenceColor(classification.resultConfidence), 0.4)}`
                      }
                    }}
                  />
                  <Typography variant="body2" fontWeight={600} sx={{
                    mt: 1.5,
                    textAlign: 'center',
                    color: getConfidenceColor(classification.resultConfidence),
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}>
                    {classification.resultConfidence?.toFixed(1)}% Confidence Score
                  </Typography>
                </Box>
              </Paper>

              {/* Technical Details */}
              <Paper elevation={8} sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: { xs: 3, sm: 4 },
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)'
              }}>
                <Typography variant={isSmall ? "h6" : "h5"} fontWeight={700} sx={{ mb: 3, color: '#43a047' }}>
                  📈 Classification Details
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: { xs: 2, sm: 2.5 },
                      background: alpha('#43a047', 0.08),
                      borderRadius: 3,
                      border: `1px solid ${alpha('#43a047', 0.2)}`
                    }}>
                      <CalendarToday sx={{ mr: 2, color: '#43a047', fontSize: { xs: 20, sm: 24 } }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Classified On
                        </Typography>
                        <Typography variant={isSmall ? "body2" : "body1"} fontWeight={600}>
                          {formatDate(classification.classifiedAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Paper elevation={2} sx={{
                      textAlign: 'center',
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3,
                      background: alpha('#ff9800', 0.08)
                    }}>
                      <Psychology sx={{ fontSize: { xs: 28, sm: 32 }, color: '#ff9800', mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                        AI Model
                      </Typography>
                      <Typography variant={isSmall ? "caption" : "body2"} fontWeight={600}>
                        {classification.aiModelVersion?.versionName || 'v1.1'}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6}>
                    <Paper elevation={2} sx={{
                      textAlign: 'center',
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3,
                      background: alpha('#2196f3', 0.08)
                    }}>
                      <Speed sx={{ fontSize: { xs: 28, sm: 32 }, color: '#2196f3', mb: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Processing
                      </Typography>
                      <Typography variant={isSmall ? "caption" : "body2"} fontWeight={600}>
                        Instant
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>

              {/* About Sortify */}
              <Paper elevation={8} sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: { xs: 3, sm: 4 },
                background: 'linear-gradient(135deg, #f8fff8, rgba(255,255,255,0.95))',
                border: '2px solid #e8f5e8',
                backdropFilter: 'blur(20px)'
              }}>
                <Typography variant={isSmall ? "h6" : "h5"} fontWeight={700} sx={{ mb: 2, color: '#43a047' }}>
                  🌱 About Sortify
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Sortify uses advanced AI technology to help classify waste and promote proper disposal practices.
                  Join us in creating a cleaner, greener Bangladesh through smart waste management.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    component={Link}
                    to="/register"
                    variant="contained"
                    fullWidth={isSmall}
                    sx={{
                      background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 1.2,
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(67,160,71,0.3)'
                    }}
                  >
                    Join Sortify
                  </Button>
                  <Button
                    component={Link}
                    to="/classify"
                    variant="outlined"
                    fullWidth={isSmall}
                    sx={{
                      borderColor: '#43a047',
                      color: '#43a047',
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 1.2,
                      borderRadius: 3,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        background: alpha('#43a047', 0.08)
                      }
                    }}
                  >
                    Try Classification
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {/* Footer CTA */}
        <Paper elevation={16} sx={{
          p: { xs: 4, sm: 6 },
          mt: { xs: 4, sm: 6 },
          borderRadius: { xs: 3, sm: 4 },
          background: 'linear-gradient(135deg, #43a047, #66bb6a)',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant={isSmall ? "h5" : "h4"} fontWeight={800} sx={{ mb: 2 }}>
              🚀 Start Your Eco Journey Today!
            </Typography>
            <Typography variant={isSmall ? "body1" : "h6"} sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              Join thousands of users making a difference through smart waste classification
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              justifyContent="center"
              sx={{ maxWidth: 500, mx: 'auto' }}
            >
              <Button
                component={Link}
                to="/"
                variant="contained"
                size="large"
                startIcon={<Home />}
                fullWidth={isSmall}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  textTransform: 'none',
                  border: '2px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(20px)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.3)'
                  }
                }}
              >
                Explore Sortify
              </Button>
              <Button
                component={Link}
                to="/classify"
                variant="contained"
                size="large"
                startIcon={<PhotoCamera />}
                fullWidth={isSmall}
                sx={{
                  background: 'rgba(255,255,255,0.95)',
                  color: '#43a047',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: 'white',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Classify Your Waste
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{
          '& .MuiSnackbarContent-root': {
            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
            borderRadius: 3,
            fontWeight: 500
          }
        }}
      />
    </Box>
  );
};

export default SharedClassificationPage;
